import type { DrivingCourseOption } from '../data/courseOptions';
import { isBraking, type VehicleInput, type VehicleState } from './vehicleDriving';

export interface BrakingResult {
  startSpeedKmh: number;
  endSpeedKmh: number;
  elapsedSeconds: number;
  distanceMetres: number;
  peakDeceleration: number;
}

export interface DrivingSessionState {
  courseId: string;
  runActive: boolean;
  runFinished: boolean;
  currentTimeSeconds: number;
  lastTimeSeconds: number | null;
  bestTimeSeconds: number | null;
  completedLaps: number;
  nextCheckpointIndex: number;
  topSpeedKmh: number;
  brakingActive: boolean;
  brakingStartSpeedKmh: number;
  brakingElapsedSeconds: number;
  brakingDistanceMetres: number;
  brakingPeakDeceleration: number;
  lastBraking: BrakingResult | null;
  observedSpawnCount: number;
}

export function createDrivingSession(
  courseId: string,
  spawnCount = 0,
  bestTimeSeconds: number | null = null
): DrivingSessionState {
  return {
    courseId,
    runActive: false,
    runFinished: false,
    currentTimeSeconds: 0,
    lastTimeSeconds: null,
    bestTimeSeconds,
    completedLaps: 0,
    nextCheckpointIndex: 0,
    topSpeedKmh: 0,
    brakingActive: false,
    brakingStartSpeedKmh: 0,
    brakingElapsedSeconds: 0,
    brakingDistanceMetres: 0,
    brakingPeakDeceleration: 0,
    lastBraking: null,
    observedSpawnCount: spawnCount,
  };
}

function enteredCheckpoint(
  previous: VehicleState,
  next: VehicleState,
  checkpoint: DrivingCourseOption['checkpoints'][number]
): boolean {
  const before = Math.hypot(previous.x - checkpoint.x, previous.z - checkpoint.z);
  const after = Math.hypot(next.x - checkpoint.x, next.z - checkpoint.z);
  return before > checkpoint.radius && after <= checkpoint.radius;
}

function finalizeBraking(session: DrivingSessionState, endSpeedKmh: number): void {
  if (session.brakingElapsedSeconds < 0.08) {
    session.lastBraking = null;
  } else {
    session.lastBraking = {
      startSpeedKmh: session.brakingStartSpeedKmh,
      endSpeedKmh,
      elapsedSeconds: session.brakingElapsedSeconds,
      distanceMetres: session.brakingDistanceMetres,
      peakDeceleration: session.brakingPeakDeceleration,
    };
  }
  session.brakingActive = false;
  session.brakingElapsedSeconds = 0;
  session.brakingDistanceMetres = 0;
  session.brakingPeakDeceleration = 0;
}

/** CarDriver가 물리 직후 호출하는 결정론적 주행 세션 업데이트입니다. */
export function stepDrivingSession(
  session: DrivingSessionState,
  previous: VehicleState,
  next: VehicleState,
  input: VehicleInput,
  dt: number,
  course: DrivingCourseOption
): DrivingSessionState {
  if (session.courseId !== course.id) {
    return createDrivingSession(course.id, next.spawnCount);
  }

  if (next.spawnCount !== session.observedSpawnCount) {
    const reset = createDrivingSession(course.id, next.spawnCount);
    reset.topSpeedKmh = session.topSpeedKmh;
    reset.bestTimeSeconds = session.bestTimeSeconds;
    reset.lastTimeSeconds = session.lastTimeSeconds;
    reset.completedLaps = session.completedLaps;
    reset.lastBraking = session.lastBraking;
    return reset;
  }

  const acceptedDt = Math.min(Math.max(dt, 0), 1 / 20);
  const speedKmh = Math.abs(next.speed) * 3.6;
  session.topSpeedKmh = Math.max(session.topSpeedKmh, speedKmh);

  if (!session.runFinished && (session.runActive || speedKmh >= 2)) {
    session.runActive = true;
    session.currentTimeSeconds += acceptedDt;
  }

  const expected = course.checkpoints[session.nextCheckpointIndex];
  if (session.runActive && expected && enteredCheckpoint(previous, next, expected)) {
    const isFinish = session.nextCheckpointIndex === course.checkpoints.length - 1;
    if (isFinish) {
      const result = session.currentTimeSeconds;
      session.lastTimeSeconds = result;
      session.bestTimeSeconds =
        session.bestTimeSeconds === null ? result : Math.min(session.bestTimeSeconds, result);
      session.completedLaps += 1;
      session.nextCheckpointIndex = 0;
      if (course.kind === 'circuit') {
        session.currentTimeSeconds = 0;
      } else {
        session.runActive = false;
        session.runFinished = true;
      }
    } else {
      session.nextCheckpointIndex += 1;
    }
  }

  const pathDistance = Math.hypot(next.x - previous.x, next.z - previous.z);
  const brakePressed = isBraking(input);
  if (brakePressed && Math.abs(previous.speed) > 1.5 && !session.brakingActive) {
    session.brakingActive = true;
    session.brakingStartSpeedKmh = Math.abs(previous.speed) * 3.6;
    session.brakingElapsedSeconds = 0;
    session.brakingDistanceMetres = 0;
    session.brakingPeakDeceleration = 0;
  }
  if (session.brakingActive) {
    session.brakingElapsedSeconds += acceptedDt;
    session.brakingDistanceMetres += pathDistance;
    const deceleration =
      acceptedDt > 0 ? Math.max(0, (Math.abs(previous.speed) - Math.abs(next.speed)) / acceptedDt) : 0;
    session.brakingPeakDeceleration = Math.max(session.brakingPeakDeceleration, deceleration);
    if (!brakePressed || Math.abs(next.speed) < 0.5) {
      finalizeBraking(session, Math.abs(next.speed) * 3.6);
    }
  }

  return session;
}

export function formatLapTime(seconds: number | null): string {
  if (seconds === null) {
    return '--:--.---';
  }
  const totalMilliseconds = Math.max(0, Math.round(seconds * 1000));
  const minutes = Math.floor(totalMilliseconds / 60_000);
  const remainingMilliseconds = totalMilliseconds - minutes * 60_000;
  const wholeSeconds = Math.floor(remainingMilliseconds / 1000);
  const milliseconds = remainingMilliseconds % 1000;
  return `${String(minutes).padStart(2, '0')}:${String(wholeSeconds).padStart(2, '0')}.${String(milliseconds).padStart(3, '0')}`;
}
